<template>
  <div class="ssp-panel">
    <div class="ssp-header">
      <div>
        <span class="ssp-title">Esquemas de distribución</span>
        <span class="ssp-subtitle">Define cómo se distribuye cada servicio entre inquilinos</span>
      </div>
      <button class="ssp-close-btn" @click="onCloseAttempt"><span class="mdi mdi-close"></span></button>
    </div>

    <div class="ssp-content">
      <div v-for="s in localSchemes" :key="s.serviceId" class="ssp-card">
        <div class="ssp-card__collapsed" @click="toggleExpand(s.serviceId)">
          <span class="mdi ssp-card__icon" :class="categoryIcon(s.category)"></span>
          <div class="ssp-card__info">
            <div class="ssp-card__name">{{ s.name }}</div>
            <span class="ssp-card__scheme-tag">{{ SCHEME_LABELS[s.distributionScheme] || 'Sin definir' }}</span>
          </div>
          <span class="mdi mdi-chevron-down ssp-card__chevron" :class="{ 'ssp-card__chevron--open': expandedIds.has(s.serviceId) }"></span>
        </div>

        <div v-if="expandedIds.has(s.serviceId)" class="ssp-card__expanded">
          <div class="ssp-pills">
            <button v-for="opt in SCHEME_OPTIONS" :key="opt.value" class="ssp-pill" :class="{ 'ssp-pill--active': s.distributionScheme === opt.value }" @click="s.distributionScheme = opt.value">
              {{ opt.label }}
            </button>
          </div>

          <!-- Proporcional por m² -->
          <table v-if="s.distributionScheme === 'PROPORTIONAL_M2'" class="ssp-table ssp-table--readonly">
            <tr v-for="t in visibleTenants(s)" :key="t.contractId">
              <td>{{ t.tenantName }} · {{ t.plotName }}</td>
              <td>{{ t.areaM2 }} m²</td>
              <td>{{ t.percentOfTotal }}%</td>
            </tr>
          </table>

          <!-- Partes iguales -->
          <div v-if="s.distributionScheme === 'EQUAL_PARTS'" class="ssp-info-note">
            Cada inquilino pagará 1/{{ visibleTenants(s).length }} del total ({{ visibleTenants(s).length }} inquilinos activos este mes).
          </div>

          <!-- Por consumo medido -->
          <template v-if="s.distributionScheme === 'METERED'">
            <div class="ssp-table-actions">
              <input :ref="(el) => (csvInputs[s.serviceId] = el)" type="file" accept=".csv" style="display: none" @change="onImportCsv(s, $event)" />
              <button class="ssp-import-btn" @click="csvInputs[s.serviceId]?.click()">Importar lecturas (CSV)</button>
            </div>
            <table class="ssp-table">
              <tr v-for="t in visibleTenants(s)" :key="t.contractId">
                <td>{{ t.tenantName }} · {{ t.plotName }}</td>
                <td><input v-model.number="readingsFor(s)[t.contractId]" type="number" class="ssp-input ssp-input--sm" /></td>
                <td>{{ meteredPercent(s, t.contractId) }}%</td>
              </tr>
            </table>
          </template>

          <!-- Personalizado -->
          <template v-if="s.distributionScheme === 'CUSTOM'">
            <table class="ssp-table">
              <tr v-for="t in visibleTenants(s)" :key="t.contractId">
                <td>{{ t.tenantName }} · {{ t.plotName }}</td>
                <td><input v-model.number="allocationsFor(s)[t.contractId]" type="number" class="ssp-input ssp-input--sm ssp-input--mono" /> %</td>
              </tr>
            </table>
            <div class="ssp-custom-total" :class="customTotalOk(s) ? 'ssp-custom-total--ok' : 'ssp-custom-total--warn'">
              Total: {{ customTotal(s) }}% {{ customTotalOk(s) ? '✓' : `· Faltan ${(100 - customTotal(s)).toFixed(1)}%` }}
            </div>
          </template>

          <!-- Exclusiones -->
          <div class="ssp-exclusions">
            <label class="ssp-exclusions-toggle">
              <input type="checkbox" v-model="exclusionsOpen[s.serviceId]" /> Excluir inquilinos
            </label>
            <div v-if="exclusionsOpen[s.serviceId]" class="ssp-exclusions-list">
              <label v-for="t in tenants" :key="t.contractId" class="ssp-exclusion-item">
                <input type="checkbox" :checked="s.excludedContractIds.includes(t.contractId)" @change="toggleExclusion(s, t.contractId)" />
                {{ t.tenantName }} · {{ t.plotName }}
              </label>
            </div>
          </div>

          <!-- Aplicación -->
          <div class="ssp-apply">
            <label class="ssp-apply-option"><input type="radio" :name="'apply-' + s.serviceId" :value="false" v-model="applyDefault[s.serviceId]" /> Aplicar solo este mes</label>
            <label class="ssp-apply-option"><input type="radio" :name="'apply-' + s.serviceId" :value="true" v-model="applyDefault[s.serviceId]" /> Aplicar como predeterminado</label>
          </div>
        </div>
      </div>
    </div>

    <div class="ssp-footer">
      <button class="ssp-save-btn" :disabled="!canSaveAll" @click="onSaveAll">Guardar configuración</button>
    </div>

    <div v-if="showDiscardConfirm" class="ssp-confirm-backdrop" @click="showDiscardConfirm = false">
      <div class="ssp-confirm" @click.stop>
        <p>Tienes cambios sin guardar en los esquemas de distribución. ¿Descartar cambios o guardar antes de cerrar?</p>
        <div class="ssp-confirm-actions">
          <button class="ssp-confirm-discard" @click="$emit('close')">Descartar</button>
          <button class="ssp-confirm-save" @click="onSaveAll">Guardar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { getSchemesConfig, saveScheme } from '@/services/services'

const props = defineProps({
  parkId: { type: String, required: true },
  period: { type: String, required: true },
})

const emit = defineEmits(['close', 'saved'])

const CATEGORY_ICONS = {
  ELECTRICITY: 'mdi-lightning-bolt', WATER: 'mdi-water', SECURITY: 'mdi-shield-outline',
  ROAD_MAINTENANCE: 'mdi-road-variant', WASTE_COLLECTION: 'mdi-recycle', LIGHTING: 'mdi-lightbulb-outline',
  GAS: 'mdi-fire', CUSTOM: 'mdi-dots-grid',
}
function categoryIcon(c) { return CATEGORY_ICONS[c] || 'mdi-dots-grid' }

const SCHEME_OPTIONS = [
  { value: 'PROPORTIONAL_M2', label: 'Proporcional por m²' },
  { value: 'EQUAL_PARTS', label: 'Partes iguales' },
  { value: 'METERED', label: 'Por consumo medido' },
  { value: 'CUSTOM', label: 'Personalizado' },
]
const SCHEME_LABELS = Object.fromEntries(SCHEME_OPTIONS.map((o) => [o.value, o.label]))

const tenants = ref([])
const localSchemes = ref([])
const expandedIds = ref(new Set())
const exclusionsOpen = reactive({})
const applyDefault = reactive({})
const readingsData = reactive({})
const allocationsData = reactive({})
const csvInputs = reactive({})
const hasChanges = ref(false)
const showDiscardConfirm = ref(false)

async function load() {
  const result = await getSchemesConfig(props.parkId, props.period)
  tenants.value = result.tenants
  localSchemes.value = result.schemes.map((s) => ({ ...s }))

  for (const s of result.schemes) {
    exclusionsOpen[s.serviceId] = s.excludedContractIds.length > 0
    applyDefault[s.serviceId] = false
    readingsData[s.serviceId] = Object.fromEntries(s.readings.map((r) => [r.contractId, r.reading]))
    allocationsData[s.serviceId] = Object.fromEntries(s.allocations.map((a) => [a.contractId, a.percent]))
  }
}
onMounted(load)

function toggleExpand(id) {
  const next = new Set(expandedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  expandedIds.value = next
}

function visibleTenants(s) {
  return tenants.value.filter((t) => !s.excludedContractIds.includes(t.contractId))
}

function toggleExclusion(s, contractId) {
  hasChanges.value = true
  if (s.excludedContractIds.includes(contractId)) {
    s.excludedContractIds = s.excludedContractIds.filter((id) => id !== contractId)
  } else {
    s.excludedContractIds = [...s.excludedContractIds, contractId]
  }
}

function readingsFor(s) { hasChanges.value = true; return readingsData[s.serviceId] }
function allocationsFor(s) { hasChanges.value = true; return allocationsData[s.serviceId] }

function meteredPercent(s, contractId) {
  const data = readingsData[s.serviceId] || {}
  const relevant = visibleTenants(s).map((t) => data[t.contractId] || 0)
  const total = relevant.reduce((a, b) => a + b, 0)
  if (total === 0) return 0
  return Math.round(((data[contractId] || 0) / total) * 1000) / 10
}

function customTotal(s) {
  const data = allocationsData[s.serviceId] || {}
  const sum = visibleTenants(s).reduce((acc, t) => acc + (data[t.contractId] || 0), 0)
  return Math.round(sum * 10) / 10
}
function customTotalOk(s) { return Math.abs(customTotal(s) - 100) < 0.05 }

function onImportCsv(s, event) {
  const file = event.target.files[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = (e) => {
    const lines = e.target.result.split('\n').map((l) => l.trim()).filter(Boolean)
    const data = readingsData[s.serviceId] || {}
    for (const line of lines) {
      const [plotName, readingStr] = line.split(',').map((x) => x.trim())
      const tenant = tenants.value.find((t) => t.plotName.toLowerCase() === plotName?.toLowerCase())
      if (tenant && readingStr) data[tenant.contractId] = parseFloat(readingStr)
    }
    readingsData[s.serviceId] = { ...data }
    hasChanges.value = true
  }
  reader.readAsText(file)
  event.target.value = ''
}

const canSaveAll = computed(() => {
  return localSchemes.value.every((s) => s.distributionScheme !== 'CUSTOM' || customTotalOk(s))
})

async function onSaveAll() {
  showDiscardConfirm.value = false
  for (const s of localSchemes.value) {
    const allocations = s.distributionScheme === 'CUSTOM'
      ? visibleTenants(s).map((t) => ({ contractId: t.contractId, percent: (allocationsData[s.serviceId] || {})[t.contractId] || 0 }))
      : undefined
    const readings = s.distributionScheme === 'METERED'
      ? visibleTenants(s).map((t) => ({ contractId: t.contractId, reading: (readingsData[s.serviceId] || {})[t.contractId] || 0 }))
      : undefined

    await saveScheme(s.serviceId, {
      parkId: props.parkId,
      distributionScheme: s.distributionScheme,
      excludedContractIds: s.excludedContractIds,
      applyAsDefault: !!applyDefault[s.serviceId],
      allocations,
      readings,
    })
  }
  emit('saved')
  emit('close')
}

function onCloseAttempt() {
  if (hasChanges.value) {
    showDiscardConfirm.value = true
  } else {
    emit('close')
  }
}
</script>

<style scoped>
.ssp-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0 1.5rem 1.5rem 1rem;
  min-height: 0;
  position: relative;
}

.ssp-header { flex-shrink: 0; height: 56px; display: flex; align-items: center; padding: 0 1.25rem; border-bottom: 1px solid #f1f5f9; }
.ssp-title {
  display: block;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 13px;
  color: #1e3a5f;
}
.ssp-subtitle {
  display: block;
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
}
.ssp-close-btn { margin-left: auto; background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }

.ssp-content { flex: 1; min-height: 0; overflow-y: auto; padding: 0.75rem; display: flex; flex-direction: column; gap: 0.5rem; }
.ssp-content::-webkit-scrollbar { width: 6px; }
.ssp-content::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.ssp-content::-webkit-scrollbar-track { background: transparent; }

.ssp-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; flex-shrink: 0; }
.ssp-card__collapsed { height: 64px; display: flex; align-items: center; gap: 0.6rem; padding: 0 14px; cursor: pointer; }
.ssp-card__icon { font-size: 24px; color: #64748b; flex-shrink: 0; }
.ssp-card__info { flex: 1; min-width: 0; }
.ssp-card__name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; }
.ssp-card__scheme-tag { font-size: 10px; background: #f1f5f9; color: #64748b; border-radius: 4px; padding: 0.05rem 0.3rem; }
.ssp-card__chevron { font-size: 12px; color: #94a3b8; transition: transform 150ms ease; }
.ssp-card__chevron--open { transform: rotate(180deg); }

.ssp-card__expanded { background: #fafafa; border-top: 1px solid #f1f5f9; padding: 0.75rem; display: flex; flex-direction: column; gap: 0.6rem; }

.ssp-pills { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.ssp-pill { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 0.3rem 0.65rem; font-family: var(--font-body); font-size: 11px; color: #64748b; cursor: pointer; }
.ssp-pill--active { background: #eff6ff; border-color: #3b82f6; color: #3b82f6; font-weight: 500; }

.ssp-table { width: 100%; border-collapse: collapse; font-family: var(--font-body); font-size: 12px; }
.ssp-table td { padding: 0.35rem 0.4rem; border-bottom: 1px solid #f1f5f9; color: #1e3a5f; }
.ssp-table--readonly td:not(:first-child) { text-align: right; font-family: var(--font-mono); }

.ssp-input { font-family: var(--font-body); font-size: 12px; border: 1px solid #e2e8f0; border-radius: 4px; padding: 0.2rem 0.4rem; width: 100%; box-sizing: border-box; }
.ssp-input--sm { width: 70px; }
.ssp-input--mono { font-family: var(--font-mono); }

.ssp-info-note { background: #eff6ff; border-radius: 6px; padding: 0.5rem 0.6rem; font-family: var(--font-body); font-size: 11px; color: #1e40af; }

.ssp-table-actions { display: flex; justify-content: flex-end; }
.ssp-import-btn { background: none; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.3rem 0.6rem; font-family: var(--font-body); font-size: 11px; color: #3b82f6; cursor: pointer; }

.ssp-custom-total { font-family: var(--font-body); font-size: 12px; font-weight: 500; padding: 0.3rem 0.5rem; border-radius: 6px; }
.ssp-custom-total--ok { background: #dcfce7; color: #16a34a; }
.ssp-custom-total--warn { background: #fffbeb; color: #d97706; }

.ssp-exclusions { border-top: 1px dashed #e2e8f0; padding-top: 0.5rem; }
.ssp-exclusions-toggle { display: flex; align-items: center; gap: 0.3rem; font-family: var(--font-body); font-size: 12px; color: #64748b; cursor: pointer; }
.ssp-exclusions-list { display: flex; flex-direction: column; gap: 0.25rem; margin-top: 0.4rem; }
.ssp-exclusion-item { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 11px; color: #1e3a5f; }

.ssp-apply { display: flex; flex-direction: column; gap: 0.3rem; border-top: 1px dashed #e2e8f0; padding-top: 0.5rem; }
.ssp-apply-option { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }

.ssp-footer { flex-shrink: 0; height: 48px; display: flex; align-items: center; padding: 0 1.25rem; border-top: 1px solid #e2e8f0; }
.ssp-save-btn { width: 100%; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; height: 34px; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.ssp-save-btn:disabled { opacity: 0.4; cursor: default; }

.ssp-confirm-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.ssp-confirm { background: #ffffff; border-radius: 10px; padding: 1.25rem; max-width: 320px; }
.ssp-confirm p { margin: 0 0 1rem; font-family: var(--font-body); font-size: 13px; color: #1e3a5f; }
.ssp-confirm-actions { display: flex; gap: 0.5rem; justify-content: flex-end; }
.ssp-confirm-discard { background: none; border: 1px solid #e2e8f0; color: #64748b; border-radius: 6px; padding: 0.4rem 0.75rem; font-family: var(--font-body); font-size: 12px; cursor: pointer; }
.ssp-confirm-save { background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; padding: 0.4rem 0.75rem; font-family: var(--font-body); font-size: 12px; cursor: pointer; }
</style>